import { createRoute, Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { ArrowLeft } from 'lucide-react'
import { useGetLeadById } from '@/application/services/lead.service'
import { Button } from '@/ui/components/common/Button'
import { Skeleton } from '@/ui/components/common/Skeleton'
import { investigationsRoute } from '@/ui/pages/management/investigations'
import { LeadHeaderSection } from './components/LeadHeaderSection'
import { LeadNotesSection } from './components/LeadNotesSection'
import { LeadFactsSection } from './components/LeadFactsSection'
import { LeadSourceSection } from '@/ui/components/leads/LeadSourceSection'

export const investigationsLeadDetailRoute = createRoute({
  getParentRoute: () => investigationsRoute,
  path: '$id/leads/$leadId',
  component: InvestigationLeadDetailPage,
})

/**
 * Management page for a single investigation lead. Acts as a thin shell that
 * extracts route params, gates rendering on the lead's loading/not-found
 * state, and composes the four section components. Each section owns its own
 * data fetching (React Query dedupes shared queries like `useGetLeadById`).
 */
export function InvestigationLeadDetailPage() {
  const { t } = useTranslation()
  const { id, leadId } = investigationsLeadDetailRoute.useParams()
  const { data: lead, isLoading: isLoadingLead } = useGetLeadById(leadId)

  if (isLoadingLead) {
    return (
      <div className="space-y-6">
        <Link to="/manage/investigations/$id/leads" params={{ id }}>
          <Button variant="outline" size="sm" leftIcon={<ArrowLeft className="w-4 h-4" />}>
            {t('admin.inv_lead_detail_back')}
          </Button>
        </Link>
        <Skeleton className="h-40 w-full rounded-lg" />
        <Skeleton className="h-8 w-1/3" />
        <Skeleton className="h-48 w-full" />
      </div>
    )
  }

  if (!lead) {
    return (
      <div className="space-y-4">
        <Link to="/manage/investigations/$id/leads" params={{ id }}>
          <Button variant="outline" size="sm" leftIcon={<ArrowLeft className="w-4 h-4" />}>
            {t('admin.inv_lead_detail_back')}
          </Button>
        </Link>
        <p className="text-muted-foreground">{t('lead_detail.not_found_title')}</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <LeadHeaderSection id={id} leadId={leadId} />
      <LeadSourceSection leadId={leadId} showManagement />
      <LeadNotesSection leadId={leadId} />
      <LeadFactsSection leadId={leadId} investigationId={id} />
    </div>
  )
}
